import React, { useEffect, useState } from 'react';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  onAuthStateChanged,
  signOut,
} from 'firebase/auth';
import { auth, brandName, getSignInMethods } from './firebase';
import { DbEntryDateProvider, DbEntryDateContext } from './contexts/DbEntryDateContext';
import { FilterProvider, useFilters } from './contexts/FilterContext';
import { HelpProvider, useHelp } from './contexts/HelpContext';
import FilterBar from './components/FilterBar';
import { HelpButton, HelpLayer } from './components/Help';
import { Spinner } from './components/ui';
import { niceDate } from './lib/format';
import './lib/charts';

import { NAV, ALL_ITEMS } from './nav';
import { planBadge, trialNotice } from './lib/tier';

// Brand comes from the backend tenant config (/api/site-config); the URL
// sniff is only a fallback for old deployments that don't serve it. Read
// lazily — the live binding is set by initFirebase(), which completes before
// the app renders (index.js awaits it) but after this module evaluates.
const getBrandName = () => brandName
  || (typeof window !== 'undefined' && window.location.href.includes('busters-sales')
    ? "Buster's Pizza"
    : 'Royal Pizza');

// The sidebar (sections, order, per-item flags) lives in nav.jsx.

const MicrosoftLogo = () => (
  <svg width="17" height="17" viewBox="0 0 21 21" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

// magicPending: the URL is a sign-in link but we don't know the email (opened
// on a different device/browser than the one that requested it) — ask for it.
function Login({ magicPending, magicError, onMagicConfirm }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [linkSentTo, setLinkSentTo] = useState('');
  const [usePassword, setUsePassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const methods = getSignInMethods();
  const brand = getBrandName();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError('Invalid email or password');
    }
  };

  const popup = async (provider, label) => {
    setError('');
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user' && err.code !== 'auth/cancelled-popup-request') {
        setError(`${label} sign-in failed`);
      }
    }
  };

  const sendLink = async () => {
    if (!email) { setError('Enter your email first'); return; }
    setBusy(true);
    setError('');
    try {
      await sendSignInLinkToEmail(auth, email, {
        url: window.location.origin + '/',
        handleCodeInApp: true,
      });
      // Remembered so the return visit can finish sign-in without re-asking.
      localStorage.setItem('sd-signin-email', email);
      setLinkSentTo(email);
    } catch (err) {
      setError(err.code === 'auth/operation-not-allowed'
        ? 'Sign-in links are not enabled for this site yet — use another method.'
        : 'Could not send the sign-in link — check the email address.');
    } finally {
      setBusy(false);
    }
  };

  const card = (children) => (
    <div className="login-wrap">
      <div className="login-card">
        <div className="logo-row">
          <div className="logo">{brand[0]}</div>
          <div>
            <h2>{brand} Reports</h2>
            <small>Multi-location sales intelligence</small>
          </div>
        </div>
        {children}
      </div>
    </div>
  );

  if (magicPending) {
    return card(
      <>
        <p className="login-info">Confirm the email address this sign-in link was sent to:</p>
        <form onSubmit={e => { e.preventDefault(); onMagicConfirm(email); }}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            autoFocus
          />
          <button type="submit" className="btn-primary">Finish sign-in</button>
          {magicError && <div className="login-error">{magicError}</div>}
        </form>
      </>
    );
  }

  if (linkSentTo) {
    return card(
      <>
        <p className="login-info">
          Check your inbox — we emailed a sign-in link to <strong>{linkSentTo}</strong>.
          Open it on this device to sign in. The link expires after a short while.
        </p>
        <button type="button" className="login-alt" onClick={() => setLinkSentTo('')}>
          ← Use a different sign-in method
        </button>
      </>
    );
  }

  const showPasswordForm = usePassword || !methods.includes('emailLink');
  return card(
    <>
      {methods.includes('google') && (
        <button type="button" className="btn-google" onClick={() => popup(new GoogleAuthProvider(), 'Google')}>
          <img
            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
            alt=""
            width={17}
            height={17}
          />
          Sign in with Google
        </button>
      )}
      {methods.includes('microsoft') && (
        <button type="button" className="btn-google" onClick={() => popup(new OAuthProvider('microsoft.com'), 'Microsoft')}>
          <MicrosoftLogo />
          Sign in with Microsoft
        </button>
      )}
      {(methods.includes('emailLink') || methods.includes('password')) && (
        <div className="login-divider">or</div>
      )}
      <form onSubmit={showPasswordForm ? handleLogin : (e) => { e.preventDefault(); sendLink(); }}>
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
        />
        {showPasswordForm ? (
          <>
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
            <button type="submit" className="btn-primary">Sign In</button>
            {methods.includes('emailLink') && (
              <button type="button" className="login-alt" onClick={() => setUsePassword(false)}>
                Email me a sign-in link instead
              </button>
            )}
          </>
        ) : (
          <>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Sending…' : 'Email me a sign-in link'}
            </button>
            {methods.includes('password') && (
              <button type="button" className="login-alt" onClick={() => setUsePassword(true)}>
                Sign in with a password instead
              </button>
            )}
          </>
        )}
        {error && <div className="login-error">{error}</div>}
      </form>
    </>
  );
}

function Shell({ user, onLogout }) {
  const [active, setActive] = useState(() => localStorage.getItem('sd-active-page') || 'overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { dbEntryDate } = React.useContext(DbEntryDateContext);
  const { me } = useFilters();
  const { setPage: setHelpPage } = useHelp();

  // Admin-only entries are hidden until /api/me confirms the role (the API
  // enforces access regardless — this is just nav hygiene). Intelligence
  // entries additionally need the tenant tier or a super-admin viewer; a
  // basic-tier tenant sees only items flagged `basic` (the API fences the
  // rest — middleware/reportTierGate.js); a bookkeeper sees only items
  // flagged for them (the Accounting section and the profile mapping on the
  // admin page).
  const isAdmin = me?.role === 'admin';
  const isBookkeeper = me?.role === 'bookkeeper';
  const hasIntel = me?.tier === 'intelligence' || !!me?.superAdmin;
  const isBasic = me?.tier === 'basic' && !me?.superAdmin;
  const showItem = (i) => (!i.adminOnly || isAdmin)
    && (!isBasic || i.basic)
    && (!i.intel || hasIntel)
    && (!i.dough || !!me?.dough)
    && (!i.accounting || me?.accounting !== false)
    && (!isBookkeeper || i.bookkeeper);
  const nav = NAV
    .map(s => ({ ...s, items: s.items.filter(showItem) }))
    .filter(s => s.items.length > 0);
  const labelFor = (item) => item.roleLabels?.[me?.role] || item.label;

  // Fall back to the first page this viewer may actually see (a bookkeeper
  // can't land on the digest).
  const firstVisible = nav[0]?.items[0] || ALL_ITEMS[0];
  const found = ALL_ITEMS.find(i => i.id === active) || firstVisible;
  const activeItem = me && !showItem(found) ? firstVisible : found;

  // Pages may ask the shell to switch (e.g. "map tenders in Accounting
  // Profiles" on the Sales Journal) without threading a callback through.
  useEffect(() => {
    const onNav = (e) => { if (e.detail) go(e.detail); };
    window.addEventListener('sd-navigate', onNav);
    return () => window.removeEventListener('sd-navigate', onNav);
  });

  // Help/tour content follows whatever page is actually rendered.
  useEffect(() => { setHelpPage(activeItem.id); }, [activeItem.id, setHelpPage]);

  const go = (id) => {
    setActive(id);
    localStorage.setItem('sd-active-page', id);
    setSidebarOpen(false);
  };

  const brand = getBrandName();
  const trial = trialNotice(me);
  return (
    <div className="shell">
      {sidebarOpen && <div className="scrim" onClick={() => setSidebarOpen(false)} />}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`} data-tour="nav">
        <div className="sidebar-brand">
          <div className="logo">{brand[0]}</div>
          <div>
            {brand}
            <small>Sales Reports</small>
          </div>
        </div>
        {nav.map(section => (
          <nav className="nav-section" key={section.section}>
            <div className="nav-section-label">{section.section}</div>
            {section.items.map(item => (
              <button
                key={item.id}
                className={`nav-item ${active === item.id ? 'active' : ''}`}
                onClick={() => go(item.id)}
              >
                <span className="icon">{item.icon}</span>
                {labelFor(item)}
                {/* Reports the paid plan doesn't include, usable thanks to a trial
                    (or a platform admin's override) */}
                {planBadge(item, me) && <span className="plan-badge">{planBadge(item, me)}</span>}
              </button>
            ))}
          </nav>
        ))}
        <div className="sidebar-footer">
          <div className="who" title={user.email}>{user.email}</div>
          <button onClick={onLogout}>Sign out</button>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <button className="menu-btn" onClick={() => setSidebarOpen(o => !o)}>☰</button>
          <h1>{labelFor(activeItem)}</h1>
          {!activeItem.noFilters && <FilterBar storesOnly={!!activeItem.storesOnly} />}
          <HelpButton />
          <div className="data-note" data-tour="datanote">
            {dbEntryDate ? `Data through ${niceDate(dbEntryDate)} snapshot` : ''}
            {activeItem.noFilters ? ' · always shows the latest business day' : ''}
            {activeItem.storesOnly ? (activeItem.accounting ? ' · the period is chosen on the page' : ' · looks ahead, so only the store picker applies') : ''}
          </div>
        </header>
        {trial && (
          <div className="trial-banner" role="status">
            <strong>{trial.headline}.</strong> {trial.detail}
            {planBadge(activeItem, me) && <> <em>This report is a {planBadge(activeItem, me)} report.</em></>}
          </div>
        )}
        <main className="content">
          {/* Wait for /api/me before mounting a page: a bookkeeper's remembered
              page would otherwise fire report requests the API rejects. */}
          {me ? activeItem.page : <Spinner label="Loading…" />}
        </main>
      </div>

      <HelpLayer pageTitle={labelFor(activeItem)} />
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [magicPending, setMagicPending] = useState(false);
  const [magicError, setMagicError] = useState('');

  const completeMagic = async (email) => {
    setMagicError('');
    try {
      await signInWithEmailLink(auth, email, window.location.href);
      localStorage.removeItem('sd-signin-email');
      setMagicPending(false);
    } catch (err) {
      setMagicError(err.code === 'auth/invalid-email'
        ? 'That email does not match this sign-in link.'
        : 'This sign-in link is invalid or has expired — request a new one.');
      setMagicPending(true); // let the user retype or bail out
    } finally {
      // Strip the one-time code from the URL either way.
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  useEffect(() => {
    // Arrived via an emailed sign-in link? Finish the sign-in. The email is
    // remembered from the device that requested the link; if this is a
    // different device, Login shows a confirm-email step.
    if (!isSignInWithEmailLink(auth, window.location.href)) return;
    const stored = localStorage.getItem('sd-signin-email');
    if (stored) completeMagic(stored);
    else setMagicPending(true);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  if (!authReady) {
    return <div className="spinner-box" style={{ minHeight: '100vh' }}><span className="spinner" /></div>;
  }

  if (!user) {
    return <Login magicPending={magicPending} magicError={magicError} onMagicConfirm={completeMagic} />;
  }

  return (
    <DbEntryDateProvider>
      <FilterProvider>
        <HelpProvider>
          <Shell user={user} onLogout={() => signOut(auth)} />
        </HelpProvider>
      </FilterProvider>
    </DbEntryDateProvider>
  );
}
